import React, { FC, useCallback, useRef } from 'react';
import { Redirect } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  Button,
  Card,
  CardBody,
  CardFooter,
  CardText,
  CardTitle,
  Col,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from 'reactstrap';
import { Config, ConventionSetting } from '../../../shared/config';
import { MaterialIcon, PageHeader, RichMarkdownIt } from '../../components';
import { UserStateComponent } from '../../components/UserState';
import { JSONForm } from '../../components/json-form/JSONForm';
import { useTranslation } from '../../translations';
import {
  DateRange,
  useBoolState,
  useConfig,
  useConvention,
  useQuery,
  useToggle,
} from '../../utils';
import { LoginForm } from './LoginForm';

function getSelectedConvention(
  params: URLSearchParams,
  config: Config,
  convention: ConventionSetting,
): ConventionSetting | null {
  let selectedConvention = convention;
  const conventionId = Number.parseInt(params.get('conventionId') ?? '', 10);

  for (const c of config.conventions) {
    // Only redirect if defaultKiosk is true, a layout is provided, and redirect isn't specified or is /
    const shouldKioskRedirect =
      c.defaultKiosk && !!c.defaultKioskLayoutId && (params.get('redirect') ?? '/') === '/';

    // If we're on the correct host for c (convention)
    if (window.location.host === c.domain) {
      // Check to see if we should redirect to kiosk mode FIRST
      if (shouldKioskRedirect) {
        window.location.replace(`${window.location.protocol}//${c.domain}/kiosk`);
        return null;
      }

      // If not, check to see if this is the primary convention, if not, redirect for login
      if (c.id !== config.activeConventionId) {
        return c;
      }
    }

    // Display the information about the correct convention if we are being redirected
    if (conventionId === c.id) {
      if (shouldKioskRedirect) {
        window.location.replace(`${window.location.protocol}//${c.domain}/kiosk`);
        return null;
      }

      selectedConvention = c;
    }
  }

  return selectedConvention;
}

export const LoginComponent: FC = () => {
  const { ts } = useTranslation();
  const config = useConfig();
  const params = useQuery();
  const convention = useConvention();
  const selectedConvention = getSelectedConvention(params, config, convention);
  const isMaintenance = config.organization.maintenanceMode || selectedConvention?.maintenanceMode;
  const [showLogin, enableShowLogin] = useBoolState(!isMaintenance);
  const [resetModalOpen, toggleResetModal] = useToggle(false);

  function onResetResult(): void {
    toast.info(
      <p>
        {ts('login.your_password_reset_request_has')}
        <br />
        {ts('login.if_your_email_is_registered')}
      </p>,
    );

    toggleResetModal();
  }

  if (!selectedConvention) {
    return null;
  }

  if (
    selectedConvention.id !== config.activeConventionId &&
    window.location.host === selectedConvention.domain
  ) {
    return (
      <UserStateComponent requireLoggedOut>
        <Redirect to="/" />
      </UserStateComponent>
    );
  }

  return (
    <UserStateComponent requireLoggedOut>
      <PageHeader className="mt-3">{ts('welcome')}</PageHeader>
      <Row className="justify-content-center">
        <Col className="mb-10" lg={3} md={6} xs={12}>
          <Card className="mb-10">
            <CardBody className="text-center">
              <CardTitle>
                <img
                  src={`/api/logo?conventionId=${selectedConvention.id}`}
                  style={{ maxWidth: '128px' }}
                />
              </CardTitle>
              <CardTitle>{selectedConvention.longName}</CardTitle>
              <CardText>
                <DateRange
                  end={selectedConvention.endAt}
                  start={selectedConvention.startAt}
                  timeZone={selectedConvention.timeZone}
                />
              </CardText>
              <CardText>{selectedConvention.venue}</CardText>
            </CardBody>
          </Card>
          {selectedConvention.loginAnnouncement && (
            <Card>
              <CardBody>
                <RichMarkdownIt source={selectedConvention.loginAnnouncement} />
              </CardBody>
            </Card>
          )}
        </Col>
        {showLogin && (
          <LoginForm onForgotPassword={toggleResetModal} showRegister={!isMaintenance} />
        )}
        {!showLogin && (
          <Col className="mb-10" id="maintenanceMode" lg={3} md={6} xs={12}>
            <Card>
              <CardBody className="text-center">
                <MaterialIcon large name="offline_bolt" type="danger" />
                <h4>{ts('login.maintenance_mode')}</h4>
                <hr />
                <p>{ts('login.we_are_currently_performing_maintenance')}</p>
                <p>{ts('common.please_check_back_later')}</p>
              </CardBody>
              <CardFooter>
                <small>
                  <Button color="secondary" id="navLogin" onClick={enableShowLogin} outline>
                    <MaterialIcon name="supervised_user_circle" />
                    {ts('login.admin_login')}
                  </Button>
                </small>
              </CardFooter>
            </Card>
          </Col>
        )}
      </Row>
      <ResetPasswordModal
        onResetResult={onResetResult}
        resetModalOpen={resetModalOpen}
        toggleResetModal={toggleResetModal}
      />
    </UserStateComponent>
  );
};

interface ResetPasswordModalProps {
  readonly resetModalOpen: boolean;
  readonly toggleResetModal: () => void;
  readonly onResetResult: () => void;
}

const ResetPasswordModal: FC<ResetPasswordModalProps> = ({
  resetModalOpen,
  toggleResetModal,
  onResetResult,
}) => {
  const { contact } = useConfig();
  const emailInput = useRef<HTMLInputElement>(null);

  const focusEmail = useCallback(() => {
    emailInput.current?.focus();
  }, []);

  const { ts } = useTranslation();
  return (
    <Modal isOpen={resetModalOpen} onOpened={focusEmail} toggle={toggleResetModal}>
      <JSONForm id="resetForm" method="post" onSuccess={onResetResult} path="/api/login/reset">
        <ModalHeader toggle={toggleResetModal}>{ts('common.reset_password')}</ModalHeader>
        <ModalBody>
          <p>{ts('login.if_you_have_forgotten_your')}</p>
          <p>{ts('login.if_you_dont_receive_an', contact.email.registration)}</p>
          <hr />
          <FormGroup row>
            <Label for="resetusernameOrMail" sm={4}>
              {ts('common.email')}
            </Label>
            <Col sm={8}>
              <Input
                id="resetusernameOrMail"
                innerRef={emailInput}
                name="usernameOrMail"
                placeholder="example@concat.app"
                required
                type="email"
              />
            </Col>
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="primary" id="resetPassword" type="submit">
            {ts('common.reset_password')}
          </Button>{' '}
          <Button color="secondary" onClick={toggleResetModal}>
            {ts('common.cancel')}
          </Button>
        </ModalFooter>
      </JSONForm>
    </Modal>
  );
};
